import { useState } from "react";
import { MapPin, Mail, Send, Phone } from "lucide-react";
import useScrollReveal from "../hooks/useScrollReveal";
import { useLanguage } from "../i18n/LanguageContext";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function Contact() {
  const [ref, isVisible] = useScrollReveal();
  const { t } = useLanguage();
  const c = t.contact;
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "", website_url: "" });
  const [status, setStatus] = useState(null);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await axios.post("/api/contact", form);
      setStatus("success");
      setForm({ name: "", email: "", company: "", message: "", website_url: "" });
    } catch (err) {
      setStatus(err?.response?.status === 429 ? "rate_limited" : "error");
    }
  };

  return (
    <section id="contact" data-testid="contact-section" ref={ref} className="bg-[#1E3455] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div className="transition-all duration-700" style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? "translateY(0)" : "translateY(24px)" }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-12 bg-[#D4AF37]" />
              <span className="text-[#D4AF37] text-xs uppercase tracking-widest">{c.label}</span>
            </div>
            <h2 className="text-4xl md:text-5xl text-white leading-tight mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {c.heading}<br /><span className="gold-gradient-text">{c.headingAccent}</span>
            </h2>
            <p className="text-slate-300 text-base leading-relaxed mb-10">{c.desc}</p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0"><MapPin size={16} className="text-[#D4AF37]" /></div>
                <div>
                  <p className="text-slate-300 text-[10px] uppercase tracking-widest mb-1">{c.officeLabel}</p>
                  <p className="text-slate-300 text-sm leading-relaxed">{c.address[0]}<br />{c.address[1]}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0"><Mail size={16} className="text-[#D4AF37]" /></div>
                <div>
                  <p className="text-slate-300 text-[10px] uppercase tracking-widest mb-1">{c.emailLabel}</p>
                  <a href="mailto:info@advaltis.com" className="text-slate-300 text-sm hover:text-[#D4AF37] transition-colors duration-200">info@advaltis.com</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0"><Phone size={16} className="text-[#D4AF37]" /></div>
                <div>
                  <p className="text-slate-300 text-[10px] uppercase tracking-widest mb-1">{c.phoneLabel}</p>
                  <a href="tel:+447717536635" className="text-slate-300 text-sm hover:text-[#D4AF37] transition-colors duration-200">+44 7717 536635</a>
                </div>
              </div>
            </div>
          </div>

          <div className="transition-all duration-700" style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? "translateY(0)" : "translateY(24px)", transitionDelay: "200ms" }}>
            <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-8">
              {/* Honeypot — hidden from real users, bots will fill it */}
              <div className="absolute opacity-0 h-0 w-0 overflow-hidden" aria-hidden="true" tabIndex={-1}>
                <label htmlFor="website_url">Website</label>
                <input id="website_url" name="website_url" type="text" value={form.website_url} onChange={handleChange} autoComplete="off" tabIndex={-1} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div>
                  <label className="text-slate-300 text-[10px] uppercase tracking-widest block mb-2">{c.formName}</label>
                  <input data-testid="contact-name" name="name" value={form.name} onChange={handleChange} required placeholder={c.formNamePh}
                    className="input-glow w-full bg-transparent border-b border-slate-700 text-slate-200 focus:border-[#D4AF37] focus:outline-none py-3 text-sm placeholder-slate-600 transition-all duration-300" />
                </div>
                <div>
                  <label className="text-slate-300 text-[10px] uppercase tracking-widest block mb-2">{c.formEmail}</label>
                  <input data-testid="contact-email" name="email" type="email" value={form.email} onChange={handleChange} required placeholder={c.formEmailPh}
                    className="input-glow w-full bg-transparent border-b border-slate-700 text-slate-200 focus:border-[#D4AF37] focus:outline-none py-3 text-sm placeholder-slate-600 transition-all duration-300" />
                </div>
              </div>
              <div>
                <label className="text-slate-300 text-[10px] uppercase tracking-widest block mb-2">{c.formCompany}</label>
                <input data-testid="contact-company" name="company" value={form.company} onChange={handleChange} placeholder={c.formCompanyPh}
                  className="input-glow w-full bg-transparent border-b border-slate-700 text-slate-200 focus:border-[#D4AF37] focus:outline-none py-3 text-sm placeholder-slate-600 transition-all duration-300" />
              </div>
              <div>
                <label className="text-slate-300 text-[10px] uppercase tracking-widest block mb-2">{c.formMessage}</label>
                <textarea data-testid="contact-message" name="message" value={form.message} onChange={handleChange} required rows={4} placeholder={c.formMessagePh}
                  className="input-glow w-full bg-transparent border-b border-slate-700 text-slate-200 focus:border-[#D4AF37] focus:outline-none py-3 text-sm placeholder-slate-600 resize-none transition-all duration-300" />
              </div>
              {status === "success" ? (
                <div data-testid="contact-success" className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-6">
                  <p className="text-[#D4AF37] text-sm font-semibold mb-1">{c.successTitle}</p>
                  <p className="text-slate-300 text-sm">{c.success}</p>
                </div>
              ) : status === "error" ? (
                <p data-testid="contact-error" className="text-red-400 text-sm">{c.error}</p>
              ) : status === "rate_limited" ? (
                <p data-testid="contact-rate-limited" className="text-red-400 text-sm">{c.rateLimited || "Too many submissions. Please try again later."}</p>
              ) : null}
              <button data-testid="contact-submit" type="submit" disabled={status === "loading"}
                className="btn-gold bg-[#D4AF37] text-[#0B1221] px-8 py-4 text-sm font-bold uppercase tracking-wider hover:bg-[#F59E0B] flex items-center gap-3 group disabled:opacity-60 disabled:cursor-not-allowed">
                {status === "loading" ? c.sending : c.submit}
                <Send size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
